import { GetSchoolsDocument } from "@/generated/graphql";
import { useQuery } from "@apollo/client/react";
import { SCHOOL_LIMIT } from "./schoolItemLimit";

/**
 * Fetches and paginates schools with search functionality.
 *
 * Queries the GraphQL API for schools with support for infinite scrolling/pagination
 * and text search by school name. The loadMore function appends additional pages
 * to the existing list for seamless scrolling.
 *
 * @param searchText - Search query to filter schools by name (case-insensitive)
 * @param limit - Schools per page; defaults to the 3x3 grid the picker modals use
 * @returns An object containing:
 *   - schools: Array of School objects for the current page(s)
 *   - loading: Boolean indicating if the initial query is in progress
 *   - loadMore: Function to fetch the next page of schools
 *   - hasMore: Boolean indicating if more schools are available to load
 *   - total: Count of schools matching the search, not just the loaded pages
 *
 * @example
 * const { schools, loading, loadMore, hasMore } = useSchools("Stanford");
 * // schools: [{ id: "1", name: "Stanford University", stage: "UNIVERSITY" }]
 */
export function useSchools(searchText: string, limit: number = SCHOOL_LIMIT) {
  const {
    data: getSchoolsData,
    loading,
    fetchMore,
  } = useQuery(GetSchoolsDocument, {
    variables: {
      limit,
      offset: 0,
      searchText: searchText || null,
    },
  });

  const items = getSchoolsData?.schools?.items ?? [];
  const hasMore = getSchoolsData?.schools.hasMore ?? false;
  const total = getSchoolsData?.schools.total ?? 0;

  const loadMore = () => {
    fetchMore({
      variables: {
        limit,
        offset: items.length,
        searchText: searchText || null,
      },

      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          schools: {
            ...fetchMoreResult.schools,
            items: [...prev.schools.items, ...fetchMoreResult.schools.items],
          },
        };
      },
    });
  };

  return { schools: items, loading, loadMore, hasMore, total };
}
